/**
 * The user's dictation-engine choice, readable synchronously from anywhere.
 *
 * The listening counterpart of `voice-engine-preference.ts`, and it exists for
 * the same reason. Starting a recording has to know the engine *synchronously*
 * — it travels with the `start_dictation` request — and awaiting a React render
 * or an IPC round-trip first would add latency to the one action where latency
 * is the whole complaint. Persistence, meanwhile, belongs in the settings
 * store, which is a Zustand store wired through `mirroredStore` and so drags in
 * window-role tracking, Tauri event listeners and cross-window mirroring, none
 * of which the dictation hotkey path should have to load.
 *
 * So the store owns persistence and pushes each change in here; the dictation
 * code reads it back out. This module deliberately imports nothing but
 * `voice-input-preference.ts`, its equally dependency-free sibling for the
 * microphone, so one call yields everything a recording start sends.
 */

import { voiceInputArgs } from "./voice-input-preference";

export type DictationEnginePreference = "system" | "whisper";

let engine: DictationEnginePreference | null = null;
/** The spoken language as a BCP-47 tag, or null for the engine default. */
let locale: string | null = null;

/** The chosen engine, or null to follow the machine default. */
export function dictationEnginePreference(): DictationEnginePreference | null {
  return engine;
}

export function setDictationEnginePreference(
  next: DictationEnginePreference | null,
): void {
  engine = next;
}

export function setDictationLocalePreference(next: string | null): void {
  locale = next;
}

/**
 * The arguments to send with a request that starts a recording.
 *
 * A chosen engine is always named, the platform recogniser included.
 * Following the default sends an EMPTY object, rather than `{ engine:
 * undefined }`, so nothing depends on serialisation dropping `undefined`. The
 * Rust side reads a missing engine as the machine default: on Windows it
 * decides that at the start itself, so the recording after the first-run
 * download already uses the new default; on a Mac it is the Apple recogniser,
 * or Whisper on an Intel Mac, which dictates with Whisper alone.
 * Live voice reads the same engine: on Whisper the speech helper still
 * captures, for its echo cancellation, and each segment is transcribed after
 * its pause. The microphone and the spoken language
 * follow the same rule: `inputDeviceId` is present only when the user picked a
 * device, `locale` only when they picked a language. Both go with every start,
 * one-shot and live voice alike.
 */
export function dictationStartArgs(): {
  engine?: string;
  inputDeviceId?: string;
  locale?: string;
} {
  return {
    ...dictationEngineArgs(),
    ...voiceInputArgs(),
    ...(locale ? { locale } : {}),
  };
}

/** The engine argument alone, for a request about the engine rather than a
 *  recording - the languages it can listen in. Empty while following the
 *  default, for the same reasons. */
export function dictationEngineArgs(): { engine?: string } {
  return engine ? { engine } : {};
}
